import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faTrash, faPlus, faSpinner, faCopy, faExclamationCircle, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

interface ApiKey {
    id: number;
    label: string;
    apiKey: string;
    created: string;
    active: boolean;
}

const ApiKeys: React.FC = () => {
    const [keys, setKeys] = useState<ApiKey[]>([]);
    const [newKeyLabel, setNewKeyLabel] = useState('');
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [deletingKeyStr, setDeletingKeyStr] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const fetchKeys = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get<ApiKey[]>('/pool/apiKeys/list');
            if (response.data) {
                setKeys(response.data.filter(k => k.active));
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load API keys');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchKeys();
    }, []);

    const showSuccess = (msg: string) => {
        setSuccess(msg);
        setTimeout(() => setSuccess(null), 3000);
    };

    const showError = (msg: string) => {
        setError(msg);
        setTimeout(() => setError(null), 5000);
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (keys.length >= 5) {
            showError('Maximum 5 API keys allowed');
            return;
        }
        if (!newKeyLabel.trim()) {
            showError('Please provide a label for the API key');
            return;
        }

        setCreating(true);
        setError(null);
        try {
            const response = await axios.post('/pool/apiKeys/new', { label: newKeyLabel.trim() });
            if (response.data && response.data.status === true) {
                showSuccess('API Key created successfully');
                setNewKeyLabel('');
                fetchKeys();
            } else {
                showError(response.data?.message || 'Failed to create API key');
            }
        } catch (err: any) {
            showError(err.response?.data?.message || 'Failed to create API key');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (apiKey: string) => {
        setDeletingKeyStr(apiKey);
        setError(null);
        try {
            const response = await axios.post('/pool/apiKeys/deActive', { key: apiKey });
            if (response.data && response.data.status === true) {
                showSuccess('API Key deleted successfully');
                fetchKeys();
            } else {
                showError(response.data?.message || 'Failed to delete API key');
            }
        } catch (err: any) {
            showError(err.response?.data?.message || 'Failed to delete API key');
        } finally {
            setDeletingKeyStr(null);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        showSuccess('API Key copied to clipboard!');
    };

    return (
        <div className="flex flex-col gap-6 p-2 md:p-6 w-full max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center">
                            <FontAwesomeIcon icon={faKey} />
                        </div>
                        API Keys
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400 mt-2">
                        Manage your API access keys. You can create up to 5 active keys at a time.
                    </p>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl flex items-center gap-3 border border-red-100 dark:border-red-900/50">
                    <FontAwesomeIcon icon={faExclamationCircle} />
                    <span>{error}</span>
                </div>
            )}

            {success && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 p-4 rounded-xl flex items-center gap-3 border border-emerald-100 dark:border-emerald-900/50">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>{success}</span>
                </div>
            )}

            <div className="bg-white dark:bg-slate-800 rounded-[2rem] p-6 shadow-sm border border-slate-100 dark:border-slate-700/50">
                <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-4 mb-8">
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="Enter a label for your new API key..."
                            value={newKeyLabel}
                            onChange={(e) => setNewKeyLabel(e.target.value)}
                            disabled={keys.length >= 5 || creating}
                            maxLength={50}
                            className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 px-5 py-3.5 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all disabled:opacity-50"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={keys.length >= 5 || creating || !newKeyLabel.trim()}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white px-8 py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all disabled:cursor-not-allowed min-w-[160px]"
                    >
                        {creating ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faPlus} />
                                <span>Create Key</span>
                            </>
                        )}
                    </button>
                </form>

                <div className="flex items-center justify-between mb-4 px-2">
                    <h3 className="font-semibold text-slate-700 dark:text-slate-300">Active Keys ({keys.length}/5)</h3>
                </div>

                <div className="space-y-4">
                    {loading ? (
                        <div className="flex justify-center items-center py-12 text-slate-400">
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        </div>
                    ) : keys.length === 0 ? (
                        <div className="text-center py-10 bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
                            <div className="w-16 h-16 mx-auto bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center text-slate-400 dark:text-slate-500 mb-4">
                                <FontAwesomeIcon icon={faKey} size="lg" />
                            </div>
                            <p className="text-slate-500 dark:text-slate-400 font-medium">No active API keys found</p>
                            <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Create one above to get started</p>
                        </div>
                    ) : (
                        keys.map((key) => (
                            <div
                                key={key.id}
                                className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 bg-slate-50 dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-700/50 hover:border-blue-300 dark:hover:border-blue-500/30 transition-all hover:shadow-md"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h4 className="font-bold text-slate-800 dark:text-slate-200 truncate">{key.label}</h4>
                                        <span className="text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-md">Active</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm font-mono text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700">
                                        <span className="truncate">{key.apiKey}</span>
                                        <button
                                            onClick={() => copyToClipboard(key.apiKey)}
                                            className="text-slate-400 hover:text-blue-500 transition-colors shrink-0"
                                            title="Copy to clipboard"
                                        >
                                            <FontAwesomeIcon icon={faCopy} />
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-3 flex items-center gap-2">
                                        Created: {new Date(key.created).toLocaleDateString()}
                                    </p>
                                </div>
                                
                                <button
                                    onClick={() => handleDelete(key.apiKey)}
                                    disabled={deletingKeyStr === key.apiKey}
                                    className="sm:opacity-0 sm:group-hover:opacity-100 transition-opacity bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2"
                                >
                                    {deletingKeyStr === key.apiKey ? (
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faTrash} />
                                            <span>Delete</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApiKeys;
